/**
 * apps/web — entry point
 *
 * Order of operations:
 * 1. Apply theme from localStorage / system preference (before first paint)
 * 2. Import token CSS (sets all CSS custom properties)
 * 3. Mount React app
 */

import React from 'react';
import { createRoot } from 'react-dom/client';

// Self-host typography and icons so the application remains complete under
// its strict production CSP and does not disclose page visits to a font CDN.
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/700.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@fontsource/ibm-plex-mono/600.css';
import 'material-symbols/outlined.css';

// ── Token CSS (Design System §9) ─────────────────────────────────────────────
// Must import before any component so CSS variables are available on first paint.
import '@budgeting101/tokens/css';
import '@budgeting101/ui-web/css';
import './styles.css';
import './styles/control-surface.css';

// ── Theme initialization (before React hydration) ────────────────────────────
// Read stored preference; fall back to OS preference; default to light.
import { initializeTheme } from './store/themeStore.js';
import { initializeLanguage } from './store/languageStore.js';

initializeTheme();
initializeLanguage();

import { App } from './App.js';

const root = document.getElementById('root');
if (!root) throw new Error('#root element not found');

createRoot(root).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).then((registration) => {
      void registration.update();
    }).catch(() => {
      // Installation is an enhancement; the finance workspace must still boot
      // normally if a browser or deployment policy blocks service workers.
    });
  });
}
